// src/pages/PostcodeQuotenew.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PostcodeInput from "../components/PostcodeInput";
import api from "../utils/api";
import { trackFunnelEvent } from "../utils/funnelTracking";

// Extract a UK-style postcode if present; otherwise return trimmed input.
const extractPostcode = (text = "") => {
  const upper = text.toUpperCase();
  const match = upper.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/);
  if (match) return match[0].replace(/\s+/g, "");
  // Heathrow keyword fallback
  if (upper.includes("HEATHROW") || upper.includes("LHR")) return "TW6";
  return text.trim();
};

function placeIdFromPlace(place) {
  return place?.place_id || place?.placeId || null;
}

function buildLocationPayload(text) {
  const cleanedText = String(text || "").trim();
  if (!cleanedText) return "";
  return extractPostcode(cleanedText);
}

const PostcodeQuotenew = ({
  presetPickup = "",
  presetDropoff = "",
  pickupPlaceholder = "Pickup postcode",
  dropoffPlaceholder = "Drop-off postcode",
}) => {
  const navigate = useNavigate();
  const handoff = useMemo(() => new URLSearchParams(typeof window !== "undefined" ? window.location.search : ""), []);
  const MIN_ENQUIRY_HOURS = 4;
  const DEFAULT_HOURS = 25; // 1 day + 1 hour

  const isoInHours = (h) => {
    const d = new Date(Date.now() + h * 60 * 60 * 1000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const [fromPostcode, setFromPostcode] = useState(() => handoff.get("pickup") || presetPickup);
  const [toPostcode, setToPostcode] = useState(() => handoff.get("dropoff") || presetDropoff);
  const [pickupPlaceId, setPickupPlaceId] = useState(null);
  const [dropoffPlaceId, setDropoffPlaceId] = useState(null);
  const [pickupTime, setPickupTime] = useState(() => {
    const value = handoff.get("pickup_time");
    if (!value) return isoInHours(DEFAULT_HOURS);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.slice(0, 16);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => new Date());
  const pickerRef = useRef(null);
  const formStartedRef = useRef(false);
  const reachedFieldsRef = useRef(new Set());
  const [errors, setErrors] = useState({});
  const [loadingQuote, setLoadingQuote] = useState(false);

  useEffect(() => {
    trackFunnelEvent("landing_view", { source: "quote_widget" });
  }, []);

  const markQuoteInteraction = (fieldName) => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackFunnelEvent("quote_form_start", { field_name: fieldName });
    }
    if (!reachedFieldsRef.current.has(fieldName)) {
      reachedFieldsRef.current.add(fieldName);
      trackFunnelEvent("quote_field_reached", { field_name: fieldName });
    }
  };

  useEffect(() => {
    setFromPostcode(handoff.get("pickup") || presetPickup);
    setPickupPlaceId(null);
  }, [presetPickup, handoff]);
  useEffect(() => {
    setToPostcode(handoff.get("dropoff") || presetDropoff);
    setDropoffPlaceId(null);
  }, [presetDropoff, handoff]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const onPointerDown = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setPickerOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPickerOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [pickerOpen]);

  const minPickupIso = isoInHours(MIN_ENQUIRY_HOURS);

  const toLocalInputValue = (date) => {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const selectedDate = useMemo(() => new Date(pickupTime), [pickupTime]);
  const minimumDate = useMemo(() => new Date(minPickupIso), [minPickupIso]);
  const selectedTime = pickupTime.slice(11, 16) || "12:00";

  const formatPickupLabel = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "Select pickup time";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatPickupAriaLabel = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "Pickup date and time";
    return `Pickup date and time: ${d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })}`;
  };

  const monthLabel = pickerMonth.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  const calendarDays = useMemo(() => {
    const year = pickerMonth.getFullYear();
    const month = pickerMonth.getMonth();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leadingBlanks = (first.getDay() + 6) % 7;

    return [
      ...Array.from({ length: leadingBlanks }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
    ];
  }, [pickerMonth]);

  const updatePickupDateTime = (datePart, timePart = selectedTime) => {
    const [hours, minutes] = timePart.split(":").map(Number);
    const next = new Date(datePart);
    next.setHours(hours || 0, minutes || 0, 0, 0);
    const nextIso = toLocalInputValue(next);
    setPickupTime(nextIso < minPickupIso ? minPickupIso : nextIso);
  };

  const validate = () => {
    if (!fromPostcode.trim() || !toPostcode.trim()) {
      trackFunnelEvent("quote_validation_error", { error_type: "pickup_or_dropoff_missing" });
      setErrors({ quote: "Please enter both pickup and drop-off postcodes." });
      return false;
    }
    if (fromPostcode.trim().length < 3 || toPostcode.trim().length < 3) {
      trackFunnelEvent("quote_validation_error", { error_type: "pickup_or_dropoff_too_short" });
      setErrors({ quote: "Please enter full pickup and drop-off postcodes (min 3 characters each)." });
      return false;
    }
    if (!pickupTime) {
      trackFunnelEvent("quote_validation_error", { error_type: "pickup_time_missing" });
      setErrors({ quote: "Please select a pickup date and time." });
      return false;
    }
    return true;
  };

  const openDateTimePicker = () => {
    setPickerMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    setPickerOpen(true);
  };

  const getQuote = async () => {
    if (!validate()) return;

    setLoadingQuote(true);
    setErrors({});

    const pickup = buildLocationPayload(fromPostcode);
    const dropoff = buildLocationPayload(toPostcode);
    trackFunnelEvent("quote_form_submit", { status: "attempted" });

    try {
      const res = await api.post("/api/get-quote-v2", {
        pickup,
        dropoff,
        pickup_place_id: pickupPlaceId || undefined,
        dropoff_place_id: dropoffPlaceId || undefined,
        pickup_time: pickupTime,
        passengers: 1,
        bags: 0,
        ajp_journey_id: handoff.get("ajp_journey_id") || undefined,
        airport_code: handoff.get("airport_code") || undefined,
        terminal: handoff.get("terminal") || undefined,
        direction: handoff.get("direction") || undefined,
        flight_number: handoff.get("flight_number") || undefined,
        flight_time: handoff.get("flight_time") || undefined,
      });

      const data = res.data || {};
      if (data.quote_id && data.access_token) {
        trackFunnelEvent("quote_generated", { quote_id: data.quote_id, status: "ok" });
        navigate(`/results/${data.quote_id}/${data.access_token}?${handoff.toString()}`);
        return;
      }

      if (data.comingSoon) {
        trackFunnelEvent("quote_generation_failed", { failure_reason: "coming_soon", status: "not_available" });
        setErrors({
          quote:
            "🚧 Sorry, we’re not yet operating full coverage for this route.<br/>" +
            "We’re expanding rapidly — please check back soon or try another nearby pickup area. 🚖",
        });
        return;
      }

      if (data.error) {
        trackFunnelEvent("quote_generation_failed", { failure_reason: data.error, status: "api_error" });
        setErrors({ quote: `⚠️ ${data.error}` });
        return;
      }

      setErrors({
        quote:
          "We’re temporarily unable to calculate a fare for this route.<br/>" +
          "Please verify your pickup and drop-off details or try again shortly.",
      });
      trackFunnelEvent("quote_generation_failed", { failure_reason: "missing_quote_response", status: "invalid_response" });
    } catch (err) {
      console.error("❌ Error fetching fare:", err);
      trackFunnelEvent("quote_generation_failed", {
        failure_reason: err.response?.data?.error || "request_failed",
        status: String(err.response?.status || "network_error"),
      });
      setErrors({
        quote:
          err.response?.data?.error ||
          "⚠️ Our system is temporarily busy. Please try again in a few moments.",
      });
    } finally {
      setLoadingQuote(false);
    }
  };

  return (
    <div className="hero-form">
      <PostcodeInput
        placeholder={pickupPlaceholder}
        value={fromPostcode}
        onChange={(value) => {
          markQuoteInteraction("pickup");
          setFromPostcode(value);
          setPickupPlaceId(null);
        }}
        onPlaceSelected={(place) => {
          const val = place.formatted_address || place.name || "";
          setFromPostcode(val);
          setPickupPlaceId(placeIdFromPlace(place));
        }}
        restrictions={{ country: "uk" }}
        inputProps={{ onFocus: () => markQuoteInteraction("pickup") }}
      />

      <PostcodeInput
        placeholder={dropoffPlaceholder}
        value={toPostcode}
        onChange={(value) => {
          markQuoteInteraction("dropoff");
          setToPostcode(value);
          setDropoffPlaceId(null);
        }}
        onPlaceSelected={(place) => {
          const val = place.formatted_address || place.name || "";
          setDropoffPlaceId(placeIdFromPlace(place));
          setToPostcode(val);
        }}
        restrictions={{ country: "uk" }}
        inputProps={{ onFocus: () => markQuoteInteraction("dropoff") }}
      />

      <div className="hero-datetime-wrap" ref={pickerRef}>
        <button
          type="button"
          className="hero-datetime hero-datetime-trigger"
          onClick={openDateTimePicker}
          onFocus={() => markQuoteInteraction("pickup_time")}
          aria-label={formatPickupAriaLabel(pickupTime)}
          aria-expanded={pickerOpen}
          title={formatPickupAriaLabel(pickupTime)}
        >
          <span>{formatPickupLabel(pickupTime)}</span>
          <span aria-hidden="true">▣</span>
        </button>

        {pickerOpen && (
          <div className="hero-datetime-popover" role="dialog" aria-label="Choose pickup date and time">
            <div className="hero-datetime-popover-head">
              <button
                type="button"
                onClick={() => setPickerMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <strong>{monthLabel}</strong>
              <button
                type="button"
                onClick={() => setPickerMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            <div className="hero-datetime-weekdays">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="hero-datetime-days">
              {calendarDays.map((day, index) => {
                if (!day) return <span key={`blank-${index}`} />;
                const disabled = day < new Date(minimumDate.getFullYear(), minimumDate.getMonth(), minimumDate.getDate());
                const selected =
                  day.getFullYear() === selectedDate.getFullYear() &&
                  day.getMonth() === selectedDate.getMonth() &&
                  day.getDate() === selectedDate.getDate();

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    disabled={disabled}
                    className={selected ? "selected" : ""}
                    onClick={() => updatePickupDateTime(day)}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <label className="hero-datetime-time">
              <span>Time</span>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => updatePickupDateTime(selectedDate, e.target.value)}
              />
            </label>

            <button type="button" className="hero-datetime-done" onClick={() => setPickerOpen(false)}>
              Done
            </button>
          </div>
        )}
      </div>

      <button type="button" className="hero-cta" onClick={getQuote} disabled={loadingQuote}>
        {loadingQuote ? "Calculating…" : "Get Quote"}
      </button>

      {errors.quote && (
        <div
          className="error-banner"
          style={{ gridColumn: "1 / -1", marginTop: 6 }}
          dangerouslySetInnerHTML={{ __html: errors.quote }}
        />
      )}
    </div>
  );
};

export default PostcodeQuotenew;
